import InputUpload from '@app/components/atoms/Images/InputUpload'
import LabelField from '@app/components/atoms/LabelField/LabelField'
import Section from '@app/components/atoms/Section/Section'
import { toTextFieldDateFormat } from '@app/libs/format'
import { getStorageData, setStorageData } from '@app/libs/useRemember'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Inertia } from '@inertiajs/inertia'
import { InertiaLink } from '@inertiajs/inertia-react'
import { Button, Grid, TextField, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import CampaignCreateSchema from './CampaignCreateSchema'
import TimeDisplaySection from './TimeDisplaySection'
import TimeSection from './TimeSection'

const GridCustomizer = styled(Grid)({
  marginBottom: '5%',
})
const TypographyCustomizer = styled(Typography)({
  color: '#d32f2f',
  paddingLeft: 10,
})
const DivCustomizer = styled('div')({
  '& .ck .ck-editor__main > .ck-editor__editable': {
    minHeight: '150px',
    borderBottomLeftRadius: '5px',
    borderBottomRightRadius: '5px',
  },
  '& .ck-rounded-corners .ck.ck-editor__top .ck-sticky-panel .ck-toolbar': {
    borderTopLeftRadius: '5px',
    borderTopRightRadius: '5px',
  },
})

const CampaignCreate = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [disableBtn, setDisableBtn] = useState(false)

  const mutation = useMutation(() => {}, {
    onSuccess: () => {
      queryClient.invalidateQueries('listCartVoucher')
    },
  })
  const timeNow = toTextFieldDateFormat(new Date())

  const vouchers = getStorageData('infoCart')
  const vouchersData = { ...vouchers.premium, ...vouchers.standard }
  const initialValues = {
    name: '',
    start_at: timeNow,
    end_at: timeNow,
    visible_from: timeNow,
    visible_to: timeNow,
    status: 'active',
    codes_per_day: '',
    time_between_redeem: '',
    description: '',
    images: [],
    line_of_credit: '',
  }
  const onSubmit = (data) => {
    setDisableBtn(true)
    setStorageData('cartVoucher', [])
    setStorageData('infoCart', [])
    mutation.mutate()
    data.images = data.images.map((item) => item.id)
    Inertia.post(route('partner.campaigns.store'), { campaign: data, vouchersData }, {})
  }

  Inertia.on('finish', () => setDisableBtn(false))

  const {
    register,
    trigger,
    control,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    defaultValues: initialValues,
    resolver: yupResolver(CampaignCreateSchema),
  })
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <GridCustomizer container spacing={2}>
        <Grid item xs={12}>
          <Section title={t('CAMPAIGN.BASIC_INFORMATION')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <LabelField required title={t('CAMPAIGN.NAME')}>
                      <TextField
                        fullWidth
                        id="name"
                        name="name"
                        size="small"
                        variant="outlined"
                        {...register('name')}
                        error={errors.name ? true : false}
                        helperText={errors.name?.message}
                        onFocus={() => trigger('name')}
                      />
                    </LabelField>
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField title={t('CAMPAIGN.DESCRIPTION')}>
                      <DivCustomizer>
                        <CKEditor
                          config={{
                            removePlugins: ['EasyImage', 'ImageUpload', 'MediaEmbed'],
                          }}
                          data={getValues('description')}
                          editor={ClassicEditor}
                          id="description"
                          name="description"
                          onChange={(value, editor) => setValue('description', editor.getData())}
                        />
                      </DivCustomizer>
                    </LabelField>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Section>
        </Grid>
        <Grid item xs={12}>
          <Section title={t('CAMPAIGN.LINE_OF_CREDIT')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <LabelField required title={t('CAMPAIGN.LINE_OF_CREDIT')}>
                      <TextField
                        fullWidth
                        id="line_of_credit"
                        name="line_of_credit"
                        size="small"
                        type="number"
                        variant="outlined"
                        {...register('line_of_credit')}
                        error={errors.line_of_credit ? true : false}
                        helperText={errors.line_of_credit?.message}
                        onFocus={() => trigger('line_of_credit')}
                      />
                    </LabelField>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Section>
        </Grid>
        <Grid item xs={12}>
          <Section title={t('CAMPAIGN.LIMIT')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <LabelField required title={t('CAMPAIGN.LIMIT_CODE_USER_PER_DAY')}>
                      <TextField
                        fullWidth
                        id="codes_per_day"
                        name="codes_per_day"
                        size="small"
                        type="number"
                        variant="outlined"
                        {...register('codes_per_day')}
                        error={errors.codes_per_day ? true : false}
                        helperText={errors.codes_per_day?.message}
                        onFocus={() => trigger('codes_per_day')}
                      />
                    </LabelField>
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField required title={t('CAMPAIGN.TIME_BETWEEN_REDEEM_CODE')}>
                      <TextField
                        fullWidth
                        id="time_between_redeem"
                        name="time_between_redeem"
                        size="small"
                        type="number"
                        variant="outlined"
                        {...register('time_between_redeem')}
                        error={errors.time_between_redeem ? true : false}
                        helperText={errors.time_between_redeem?.message}
                        onFocus={() => trigger('time_between_redeem')}
                      />
                    </LabelField>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title={t('CAMPAIGN.TIME')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <TimeSection errors={errors} getValues={getValues} register={register} setValue={setValue} />
              </Grid>
            </Grid>
          </Section>
        </Grid>
        <Grid item xs={12}>
          <Section title={t('CAMPAIGN.TIME_DISPLAY')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <TimeDisplaySection errors={errors} getValues={getValues} register={register} setValue={setValue} />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title={t('CAMPAIGN.IMAGE')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <LabelField required recommend={t('VOUCHER.SLIDER_RECOMMEND')} title={t('CAMPAIGN.IMAGE')}>
                      <Controller
                        control={control}
                        name="images"
                        render={({ field }) => (
                          <InputUpload
                            {...field}
                            multiple
                            buttonName={t('VOUCHER.UPLOAD_IMAGE')}
                            clearErrors={clearErrors}
                            handleImageChange={(files) => setValue('images', files)}
                            id="images"
                            name="images"
                            ref={field.ref}
                            setError={setError}
                            title={t('VOUCHER.UPLOAD_IMAGE')}
                            value={getValues('images')}
                          />
                        )}
                      />

                      {errors.images && (
                        <TypographyCustomizer variant="caption">{errors.images?.message}</TypographyCustomizer>
                      )}
                    </LabelField>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Section>
        </Grid>
        <Grid item xs="auto">
          <Button color="primary" disabled={disableBtn} type="submit" variant="contained">
            {t('BUTTON.FINISH')}
          </Button>
        </Grid>

        <Grid item xs="auto">
          <Button color="inherit" component={InertiaLink} href={route('partner.cart')} variant="contained">
            {t('BUTTON.CANCEL')}
          </Button>
        </Grid>
      </GridCustomizer>
    </form>
  )
}

export default CampaignCreate
